// Camada de I/O da importação de cancelamentos: resolve o que a planilha diz
// contra o que o banco tem (UC, vendedor, distrato já existente, solicitação
// já gravada) e escreve o lote.
//
// A decisão de COMO interpretar cada célula está em cancelamentosImport.ts
// (puro, testado). Aqui só entra o que precisa de banco.
import type {
  MotivoCancelamento, StatusSolicitacaoCancelamento, DistratoExterno,
} from './cancelamentos';
import type { LinhaPlanilha } from './cancelamentosImport';

/** Situações em que a UC ainda participa do rateio — usado para achar divergência. */
const SITUACOES_ATIVAS = ['Ativa', 'Ativa-S/-Rateio', '1° Ciclo', '2° Ciclo', 'Reativacao', 'Pausada'];

export interface UcResolvida {
  id: number;
  numero_uc: string;
  nome_cliente: string | null;
  cpf_cnpj_cliente: string | null;
  situacao: string | null;
}

export type ResultadoLinha = 'pronta' | 'divergente' | 'duplicada' | 'sem_uc';

export interface LinhaResolvida {
  origem: LinhaPlanilha;
  resultado: ResultadoLinha;
  uc: UcResolvida | null;
  vendedorId: string | null;
  vendedorNaoEncontrado: boolean;
  /** Termo já existente no sistema para a mesma UC — vira o elo formal. */
  termoLogId: number | null;
  motivoCategoria: MotivoCancelamento;
  status: StatusSolicitacaoCancelamento;
  /** Explicação curta do porquê a linha não é 'pronta'. */
  impedimento: string | null;
}

export interface PreviaImportacao {
  linhas: LinhaResolvida[];
  duplicadasNaPlanilha: number;
  totais: Record<ResultadoLinha, number>;
}

const CHUNK = 80;
const soDigitos = (v: unknown) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');

async function emLotes<T, R>(itens: T[], tamanho: number, fn: (parte: T[]) => Promise<R[]>): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < itens.length; i += tamanho) out.push(...await fn(itens.slice(i, i + tamanho)));
  return out;
}

/**
 * UC da planilha → cadastro. O casamento é por dígitos, dos DOIS lados:
 * `numero_uc` no banco convive com formatação e a planilha traz apóstrofo de
 * Excel, ponto e espaço. `outro_numero_uc` também conta — é o mesmo par de
 * chaves que `ucCodigoKeys` usa no resto do sistema.
 */
export async function resolverUcs(ucsNormalizadas: string[]): Promise<Map<string, UcResolvida>> {
  const mapa = new Map<string, UcResolvida>();
  const alvo = new Set(ucsNormalizadas.filter(Boolean));
  if (alvo.size === 0) return mapa;
  const { supabase } = await import('./supabase');

  const linhas = await emLotes([...alvo], CHUNK, async parte => {
    const lista = parte.join(',');
    const { data, error } = await supabase
      .from('unidades_consumidoras')
      .select('id, numero_uc, outro_numero_uc, nome_cliente, cpf_cnpj_cliente, situacao')
      .or(`numero_uc.in.(${lista}),outro_numero_uc.in.(${lista})`);
    if (error) throw error;
    return (data || []) as any[];
  });

  linhas.forEach(u => {
    [u.numero_uc, u.outro_numero_uc].forEach(cod => {
      const k = soDigitos(cod);
      // Primeira vitória vence: se duas UCs do cadastro apontam para o mesmo
      // número (acontece com `outro_numero_uc` reaproveitado), sobrescrever
      // trocaria o vínculo de forma imprevisível conforme a ordem do retorno.
      if (k && alvo.has(k) && !mapa.has(k)) {
        mapa.set(k, {
          id: u.id, numero_uc: u.numero_uc, nome_cliente: u.nome_cliente,
          cpf_cnpj_cliente: u.cpf_cnpj_cliente, situacao: u.situacao,
        });
      }
    });
  });
  return mapa;
}

/** Nome de vendedor da planilha → `profiles.id`, tolerante a acento e caixa. */
export async function resolverVendedores(nomes: string[]): Promise<Map<string, string>> {
  const { chaveNome } = await import('./cancelamentosImport');
  const mapa = new Map<string, string>();
  const alvo = new Set(nomes.map(n => chaveNome(n)).filter(Boolean));
  if (alvo.size === 0) return mapa;

  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.from('profiles').select('id, full_name').neq('role', 'consumidor');
  if (error) throw error;

  (data || []).forEach((p: any) => {
    const k = chaveNome(p.full_name);
    if (k && alvo.has(k) && !mapa.has(k)) mapa.set(k, p.id);
  });
  return mapa;
}

/**
 * Distrato já registrado no sistema para a UC. Vale mais que o campo de texto
 * da planilha: quando existe log, a importação preenche
 * `termo_cancelamento_log_id` e deixa `distrato_externo` nulo — senão a tela
 * passaria a ter duas fontes de verdade sobre o mesmo distrato.
 */
export async function resolverTermosExistentes(ucIds: number[]): Promise<Map<number, number>> {
  const mapa = new Map<number, number>();
  if (ucIds.length === 0) return mapa;
  const { supabase } = await import('./supabase');

  const linhas = await emLotes([...new Set(ucIds)], CHUNK, async parte => {
    const { data, error } = await supabase
      .from('termo_cancelamento_logs')
      .select('id, uc_id, created_at')
      .in('uc_id', parte)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as any[];
  });

  // Vem ordenado desc por lote; o primeiro de cada UC é o termo mais recente.
  linhas.forEach(t => { if (t.uc_id && !mapa.has(t.uc_id)) mapa.set(t.uc_id, t.id); });
  return mapa;
}

/** Pares uc_id|data já gravados — o índice único da tabela usa a mesma chave. */
export async function resolverJaImportadas(ucIds: number[]): Promise<Set<string>> {
  const chaves = new Set<string>();
  if (ucIds.length === 0) return chaves;
  const { supabase } = await import('./supabase');

  const linhas = await emLotes([...new Set(ucIds)], CHUNK, async parte => {
    const { data, error } = await supabase
      .from('solicitacoes_cancelamento')
      .select('uc_id, data_solicitacao')
      .in('uc_id', parte);
    if (error) throw error;
    return (data || []) as any[];
  });
  linhas.forEach(s => chaves.add(`${s.uc_id}|${String(s.data_solicitacao).slice(0, 10)}`));
  return chaves;
}

/**
 * Monta a prévia da importação. Nada é gravado aqui — a tela mostra o
 * resultado e só então o usuário confirma.
 *
 * Uma linha vira `divergente` (e não `pronta`) quando a planilha a trata como
 * cancelamento concluído mas a UC continua ativa no cadastro. São casos reais
 * — 23 na planilha de referência — e importar calado enterraria a divergência
 * dentro do histórico como se fosse fato consumado. Divergente não é erro:
 * é importável, mas só por escolha explícita na tela.
 */
export async function montarPrevia(linhasPlanilha: LinhaPlanilha[]): Promise<PreviaImportacao> {
  const { deduplicar, chaveNome } = await import('./cancelamentosImport');
  const { linhas: unicas, removidas } = deduplicar(linhasPlanilha);

  const ucs = await resolverUcs(unicas.map(l => l.ucNormalizada));
  const vendedores = await resolverVendedores(unicas.map(l => l.vendedorNome || ''));
  const ucIds = unicas.map(l => ucs.get(l.ucNormalizada)?.id).filter((v): v is number => !!v);
  const [termos, jaImportadas] = await Promise.all([
    resolverTermosExistentes(ucIds),
    resolverJaImportadas(ucIds),
  ]);

  const linhas: LinhaResolvida[] = unicas.map(origem => {
    const uc = ucs.get(origem.ucNormalizada) ?? null;
    const vendedorNome = origem.vendedorNome || '';
    const vendedorId = vendedorNome ? (vendedores.get(chaveNome(vendedorNome)) ?? null) : null;

    let resultado: ResultadoLinha = 'pronta';
    let impedimento: string | null = null;

    if (!uc) {
      resultado = 'sem_uc';
      impedimento = origem.ucNormalizada
        ? `UC ${origem.ucBruta} não encontrada no cadastro.`
        : 'Linha sem número de UC.';
    } else if (jaImportadas.has(`${uc.id}|${origem.dataSolicitacao ?? ''}`)) {
      resultado = 'duplicada';
      impedimento = 'Já existe solicitação para esta UC nesta data.';
    } else if (
      (origem.status === 'concluida' || origem.status === 'em_andamento') &&
      SITUACOES_ATIVAS.includes(uc.situacao || '')
    ) {
      resultado = 'divergente';
      impedimento = `A planilha trata como cancelada, mas a UC está "${uc.situacao}" no cadastro.`;
    }

    return {
      origem,
      resultado,
      uc,
      vendedorId,
      vendedorNaoEncontrado: !!vendedorNome && !vendedorId,
      termoLogId: uc ? (termos.get(uc.id) ?? null) : null,
      motivoCategoria: origem.motivoCategoria,
      status: origem.status,
      impedimento,
    };
  });

  const totais: Record<ResultadoLinha, number> = { pronta: 0, divergente: 0, duplicada: 0, sem_uc: 0 };
  linhas.forEach(l => { totais[l.resultado]++; });
  return { linhas, duplicadasNaPlanilha: removidas, totais };
}

export interface ResultadoGravacao {
  loteId: string;
  gravadas: number;
  falhas: Array<{ uc: string; erro: string }>;
}

/**
 * Grava as linhas escolhidas como um lote identificado. `origem: 'importacao'`
 * + `import_lote_id` são o que torna o lote desfazível — sem eles não há como
 * distinguir o histórico importado do que a equipe digitou na tela.
 */
export async function gravarLote(
  selecionadas: LinhaResolvida[], nomeArquivo: string, userId: string | undefined,
): Promise<ResultadoGravacao> {
  const { supabase } = await import('./supabase');
  const loteId = crypto.randomUUID();

  const registros = selecionadas.filter(l => l.uc).map(l => {
    const o = l.origem;
    const temTermoNoSistema = l.termoLogId !== null;
    return {
      uc_id: l.uc!.id,
      uc_numero: l.uc!.numero_uc,
      cliente_nome: o.cliente || l.uc!.nome_cliente || l.uc!.numero_uc,
      cpf_cnpj_cliente: o.cpfCnpj || l.uc!.cpf_cnpj_cliente || null,
      // A tabela exige data; linha sem data legível herda a do cadastro só na
      // ausência total — a prévia já avisou que a data não foi lida.
      data_solicitacao: o.dataSolicitacao || new Date().toISOString().slice(0, 10),
      motivo_categoria: l.motivoCategoria,
      motivo_detalhe: o.motivoTexto,
      vendedor_responsavel_id: l.vendedorId,
      status: l.status,
      concluida_em: (l.status === 'concluida' || l.status === 'cancelada')
        ? `${o.dataRetiradaRateio || o.dataSolicitacao || new Date().toISOString().slice(0, 10)}T12:00:00Z`
        : null,
      termo_cancelamento_log_id: l.termoLogId,
      // Só registra distrato externo quando NÃO há termo no sistema — o log é
      // a fonte de verdade quando existe.
      distrato_externo: temTermoNoSistema ? null : (o.distrato as DistratoExterno | null),
      distrato_externo_data: temTermoNoSistema ? null : o.distratoData,
      distrato_externo_ref: temTermoNoSistema ? null : o.distratoRef,
      saldo_kwh_informado: o.saldoKwh,
      debitos_abertos_valor: o.debitosValor,
      multa_valor: o.multaValor,
      multa_situacao: o.multaSituacao,
      data_retirada_rateio: o.dataRetiradaRateio,
      observacao: o.observacao,
      origem: 'importacao',
      import_lote_id: loteId,
      import_origem_planilha: `${nomeArquivo} — ${o.aba} (linha ${o.linha})`,
      situacao_divergente: l.resultado === 'divergente',
      created_by: userId || null,
    };
  });

  const falhas: ResultadoGravacao['falhas'] = [];
  let gravadas = 0;
  // Em blocos: um INSERT único de 200+ linhas falha inteiro por causa de uma
  // linha ruim, e a equipe fica sem saber qual.
  for (let i = 0; i < registros.length; i += 50) {
    const bloco = registros.slice(i, i + 50);
    const { error } = await supabase.from('solicitacoes_cancelamento').insert(bloco);
    if (!error) { gravadas += bloco.length; continue; }
    // Bloco recusado: reprocessa linha a linha para isolar a culpada.
    for (const reg of bloco) {
      const { error: e1 } = await supabase.from('solicitacoes_cancelamento').insert(reg);
      if (e1) falhas.push({ uc: reg.uc_numero, erro: e1.message });
      else gravadas++;
    }
  }
  return { loteId, gravadas, falhas };
}

export interface LoteImportado {
  loteId: string;
  quantidade: number;
  criadoEm: string;
  arquivo: string | null;
}

export async function listarLotes(): Promise<LoteImportado[]> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('solicitacoes_cancelamento')
    .select('import_lote_id, created_at, import_origem_planilha')
    .eq('origem', 'importacao')
    .order('created_at', { ascending: false });
  if (error) throw error;

  const porLote = new Map<string, LoteImportado>();
  (data || []).forEach((r: any) => {
    if (!r.import_lote_id) return;
    const atual = porLote.get(r.import_lote_id);
    if (atual) { atual.quantidade++; return; }
    porLote.set(r.import_lote_id, {
      loteId: r.import_lote_id,
      quantidade: 1,
      criadoEm: r.created_at,
      // "arquivo.xlsx — AGOSTO 2026 (linha 4)" → só o nome do arquivo.
      arquivo: (r.import_origem_planilha || '').split(' — ')[0] || null,
    });
  });
  return [...porLote.values()];
}

/** Desfaz um lote inteiro. A policy de DELETE só alcança `origem='importacao'`. */
export async function desfazerLote(loteId: string): Promise<number> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase
    .from('solicitacoes_cancelamento')
    .delete()
    .eq('import_lote_id', loteId)
    .eq('origem', 'importacao')
    .select('id');
  if (error) throw error;
  return (data || []).length;
}
